// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Select from "@insite/mobius/Select";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import Block from "@store/Library/Components/Toolkit/Block";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import loadValveOptions from "@store/Framework/Store/Store/Data/ValveConfigurator/Handlers/LoadValveOptions";
import loadValveSelectors from "@store/Framework/Store/Store/Data/ValveConfigurator/Handlers/LoadValveSelectors";
import submitValveConfigure from "@store/Framework/Store/Store/Data/ValveConfigurator/Handlers/SubmitValveConfigure";
import { ValveTypeModel } from "@store/Framework/Types/ValveConfiguratorApiModels";
import React, { ChangeEvent, FC, FormEvent, useEffect, useMemo } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const v = state.store.data.valveConfigurator;
    return {
        selectors: v.selectors,
        isLoadingSelectors: v.isLoadingSelectors,
        selectedSeries: v.selectedSeries,
        selectedSize: v.selectedSize,
        selectedActuator: v.selectedActuator,
        selectedGateMaterial: v.selectedGateMaterial,
        selectedElastomer: v.selectedElastomer,
        availableSizes: v.availableSizes,
        availableActuators: v.availableActuators,
        availableGateMaterials: v.availableGateMaterials,
        availableElastomers: v.availableElastomers,
        flangeDrillingLabel: v.flangeDrillingLabel,
        readOnlyFields: v.readOnlyFields,
        isLoadingOptions: v.isLoadingOptions,
        isSubmitting: v.isSubmitting,
        error: v.error,
    };
};

const mapDispatchToProps = {
    loadValveSelectors,
    loadValveOptions,
    submitValveConfigure,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const FormWrapper = styled.form`
    border: 1px solid ${getColor("common.border")};
    border-radius: ${pxToRem(10)};
    padding: ${pxToRem(24)};
    background: ${getColor("common.background")};
`;

const FieldStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(20)};
`;

const Field = styled.div`
    display: block;
    width: 100%;

    & > * {
        width: 100%;
    }
`;

const SubmitRow = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: ${pxToRem(4)};

    @media (max-width: 991px) {
        display: none;
    }
`;

// Sticky-within-form submit bar shown only on mobile (<= 991px). Uses
// position: sticky (not fixed) so the button hugs the viewport bottom while
// the form is in view and scrolls away naturally once the user passes the end
// of the form. Negative margins break out of FormWrapper's padding so the bar
// spans edge-to-edge of the form card.
const MobileStickySubmit = styled.div`
    display: none;

    @media (max-width: 991px) {
        display: block;
        position: sticky;
        bottom: 0;
        z-index: 5;
        margin: ${pxToRem(16)} ${pxToRem(-24)} ${pxToRem(-24)};
        background: ${getColor("common.background")};
        border-top: 1px solid ${getColor("common.border")};
        padding: ${pxToRem(12)} ${pxToRem(16)};
        padding-bottom: max(${pxToRem(12)}, env(safe-area-inset-bottom));
        box-shadow: 0 -4px 12px -8px rgba(0, 0, 0, 0.15);

        button {
            width: 100%;
        }
    }
`;

const SectionLabel = styled.div`
    font-size: ${pxToRem(13)};
    font-weight: 600;
    margin: 0 0 ${pxToRem(8)} 0;
    color: ${getColor("text.main")};
`;

const RequiredMark = styled.span`
    color: ${getColor("danger.main")};
    margin-left: ${pxToRem(2)};
`;

const TypeCardGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${pxToRem(12)};
    margin-bottom: ${pxToRem(20)};
`;

const TypeCard = styled.button<{ $selected: boolean }>`
    display: flex;
    align-items: center;
    gap: ${pxToRem(12)};
    padding: ${pxToRem(12)};
    border-radius: ${pxToRem(8)};
    background: ${getColor("common.background")};
    text-align: left;
    cursor: pointer;
    transition: border-color 150ms ease;
    border: ${({ $selected }) =>
        $selected
            ? css`2px solid ${getColor("primary")}`
            : css`1px solid ${getColor("common.border")}`};

    &:hover {
        border-color: ${getColor("primary")};
    }
`;

const TypeCardImage = styled.div`
    width: ${pxToRem(48)};
    height: ${pxToRem(64)};
    flex-shrink: 0;
    background: ${getColor("common.accent")};
    border-radius: ${pxToRem(4)};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }
`;

const ReadOnlyValue = styled.div`
    border: 1px solid ${getColor("common.border")};
    background: ${getColor("common.accent")};
    border-radius: ${pxToRem(4)};
    padding: ${pxToRem(10)} ${pxToRem(12)};
    font-size: ${pxToRem(14)};
    color: ${getColor("text.main")};
    min-height: ${pxToRem(40)};
    display: flex;
    align-items: center;
`;

const ReadOnlyNote = styled.div`
    font-size: ${pxToRem(12)};
    font-style: italic;
    color: ${getColor("text.accent")};
    margin-top: ${pxToRem(4)};
`;

const QtoHint = styled.div`
    font-size: ${pxToRem(12)};
    color: ${getColor("warning.main")};
    background: ${getColor("warning.light")};
    border: 1px solid ${getColor("warning.main")};
    border-radius: ${pxToRem(4)};
    padding: ${pxToRem(6)} ${pxToRem(8)};
    margin-top: ${pxToRem(6)};
`;

const ErrorBanner = styled.div`
    border: 1px solid ${getColor("danger.main")};
    background: ${getColor("danger.light")};
    color: ${getColor("danger.main")};
    border-radius: ${pxToRem(4)};
    padding: ${pxToRem(10)} ${pxToRem(12)};
    margin-bottom: ${pxToRem(12)};
    font-size: ${pxToRem(13)};
`;

function valveTypesForUi(selectors: { series: string[]; valveTypes?: ValveTypeModel[] } | null): ValveTypeModel[] {
    if (!selectors) {
        return [];
    }

    if (selectors.valveTypes && selectors.valveTypes.length > 0) {
        return selectors.valveTypes;
    }

    return (selectors.series ?? []).map(code => ({
        label: code,
        code,
        altCode: null,
        subtitle: null,
        previewImageUrl: null,
    }));
}

const ValveConfiguratorFormWidget: FC<Props> = ({
    selectors,
    isLoadingSelectors,
    selectedSeries,
    selectedSize,
    selectedActuator,
    selectedGateMaterial,
    selectedElastomer,
    availableSizes,
    availableActuators,
    availableGateMaterials,
    availableElastomers,
    flangeDrillingLabel,
    readOnlyFields,
    isLoadingOptions,
    isSubmitting,
    error,
    loadValveSelectors,
    loadValveOptions,
    submitValveConfigure,
}) => {
    const dispatch = useDispatch();

    useEffect(() => {
        if (!selectors && !isLoadingSelectors) {
            loadValveSelectors({});
        }
    }, [selectors, isLoadingSelectors, loadValveSelectors]);

    useEffect(() => {
        if (selectedSeries && selectedSize !== null) {
            loadValveOptions({ series: selectedSeries, size: selectedSize });
        }
    }, [selectedSeries, selectedSize, loadValveOptions]);

    const valveTypes = useMemo(() => valveTypesForUi(selectors), [selectors]);

    const selectedActuatorObj = useMemo(
        () => availableActuators.find(a => a.label === selectedActuator),
        [availableActuators, selectedActuator],
    );
    const selectedGateObj = useMemo(
        () => availableGateMaterials.find(g => g.label === selectedGateMaterial),
        [availableGateMaterials, selectedGateMaterial],
    );
    const selectedElastomerObj = useMemo(
        () => availableElastomers.find(e => e.label === selectedElastomer),
        [availableElastomers, selectedElastomer],
    );

    const canSubmit =
        Boolean(selectedSeries) &&
        selectedSize !== null &&
        Boolean(selectedActuator) &&
        Boolean(selectedGateMaterial) &&
        Boolean(selectedElastomer) &&
        !isSubmitting;

    const handleSeriesChange = (code: string) => {
        dispatch({ type: "Data/ValveConfigurator/SetSeries", series: code });
    };

    const handleSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const v = e.target.value;
        dispatch({ type: "Data/ValveConfigurator/SetSize", size: v === "" ? null : Number(v) });
    };

    const handleActuatorChange = (e: ChangeEvent<HTMLSelectElement>) => {
        dispatch({ type: "Data/ValveConfigurator/SetActuator", actuator: e.target.value || null });
    };

    const handleGateChange = (e: ChangeEvent<HTMLSelectElement>) => {
        dispatch({
            type: "Data/ValveConfigurator/SetGateMaterial",
            gateMaterial: e.target.value || null,
        });
    };

    const handleElastomerChange = (e: ChangeEvent<HTMLSelectElement>) => {
        dispatch({ type: "Data/ValveConfigurator/SetElastomer", elastomer: e.target.value || null });
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        if (
            !selectedSeries ||
            selectedSize === null ||
            !selectedActuator ||
            !selectedGateMaterial ||
            !selectedElastomer
        ) {
            return;
        }

        submitValveConfigure({
            series: selectedSeries,
            size: selectedSize,
            actuatorType: selectedActuator,
            gateMaterial: selectedGateMaterial,
            wearSleeveElastomer: selectedElastomer,
        });
    };

    return (
        <Block>
            <FormWrapper onSubmit={handleSubmit}>
                <Typography
                    variant="h2"
                    css={css`
                        margin: 0 0 ${pxToRem(8)} 0;
                    `}
                >
                    {translate("FlsValveConfigurator.Form.Title")}
                </Typography>
                <Typography
                    variant="p"
                    css={css`
                        color: ${getColor("text.accent")};
                        margin: 0 0 ${pxToRem(20)} 0;
                    `}
                >
                    {translate("FlsValveConfigurator.Form.RequiredHint")}
                </Typography>

                {error ? <ErrorBanner>{error}</ErrorBanner> : null}

                <SectionLabel>
                    {translate("FlsValveConfigurator.ValveType.Label")}
                    <RequiredMark>*</RequiredMark>
                </SectionLabel>
                <TypeCardGrid>
                    {valveTypes.map(v => (
                        <TypeCard
                            key={v.code}
                            type="button"
                            $selected={selectedSeries === v.code}
                            onClick={() => handleSeriesChange(v.code)}
                            data-test-selector={`valveType_${v.code}`}
                        >
                            <TypeCardImage>
                                {v.previewImageUrl ? (
                                    <img src={v.previewImageUrl} alt={v.label} />
                                ) : (
                                    <Typography
                                        variant="legend"
                                        css={css`
                                            color: ${getColor("text.accent")};
                                        `}
                                    >
                                        {v.code}
                                    </Typography>
                                )}
                            </TypeCardImage>
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <Typography variant="p" weight="bold">
                                    {v.label}
                                </Typography>
                                {v.subtitle ? (
                                    <Typography
                                        variant="legend"
                                        css={css`
                                            color: ${getColor("text.accent")};
                                        `}
                                    >
                                        {v.subtitle}
                                    </Typography>
                                ) : null}
                            </div>
                        </TypeCard>
                    ))}
                </TypeCardGrid>

                <FieldStack>
                    <Field>
                        <Select
                            label={translate("FlsValveConfigurator.Size.Label")}
                            required
                            value={selectedSize === null ? "" : String(selectedSize)}
                            onChange={handleSizeChange}
                            disabled={!selectedSeries}
                            data-test-selector="valveSize"
                        >
                            <option value="">{translate("FlsValveConfigurator.Size.Placeholder")}</option>
                            {availableSizes.map(s => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </Select>
                    </Field>

                    <Field>
                        <SectionLabel>{translate("FlsValveConfigurator.FlangeDrilling.Label")}</SectionLabel>
                        <ReadOnlyValue>
                            {selectedSize === null
                                ? "—"
                                : isLoadingOptions
                                  ? translate("FlsValveConfigurator.Loading")
                                  : flangeDrillingLabel || "—"}
                        </ReadOnlyValue>
                    </Field>

                    <Field>
                        <Select
                            label={translate("FlsValveConfigurator.Actuator.Label")}
                            required
                            value={selectedActuator ?? ""}
                            onChange={handleActuatorChange}
                            disabled={!selectedSize || isLoadingOptions}
                            data-test-selector="valveActuator"
                        >
                            <option value="">{translate("FlsValveConfigurator.Actuator.Placeholder")}</option>
                            {availableActuators.map(a => (
                                <option key={a.label} value={a.label}>
                                    {a.label}
                                    {a.quoteToOrder ? " (Quote to Order)" : ""}
                                </option>
                            ))}
                        </Select>
                        {selectedActuatorObj?.quoteToOrder ? (
                            <QtoHint>{translate("FlsValveConfigurator.QuoteToOrder.Hint")}</QtoHint>
                        ) : null}
                    </Field>

                    <Field>
                        <Select
                            label={translate("FlsValveConfigurator.GateMaterial.Label")}
                            required
                            value={selectedGateMaterial ?? ""}
                            onChange={handleGateChange}
                            disabled={!selectedSeries || isLoadingOptions}
                            data-test-selector="valveGateMaterial"
                        >
                            <option value="">{translate("FlsValveConfigurator.GateMaterial.Placeholder")}</option>
                            {availableGateMaterials.map(g => (
                                <option key={g.label} value={g.label}>
                                    {g.label}
                                    {g.quoteToOrder ? " (Quote to Order)" : ""}
                                </option>
                            ))}
                        </Select>
                        {selectedGateObj?.quoteToOrder ? (
                            <QtoHint>{translate("FlsValveConfigurator.QuoteToOrder.Hint")}</QtoHint>
                        ) : null}
                    </Field>

                    <Field>
                        <SectionLabel>{translate("FlsValveConfigurator.GateCoating.Label")}</SectionLabel>
                        <ReadOnlyValue>{readOnlyFields?.gateCoating?.value?.trim() || "—"}</ReadOnlyValue>
                        {readOnlyFields?.gateCoating?.note ? (
                            <ReadOnlyNote>{readOnlyFields.gateCoating.note}</ReadOnlyNote>
                        ) : null}
                    </Field>

                    <Field>
                        <Select
                            label={translate("FlsValveConfigurator.Elastomer.Label")}
                            required
                            value={selectedElastomer ?? ""}
                            onChange={handleElastomerChange}
                            disabled={!selectedSeries || isLoadingOptions}
                            data-test-selector="valveElastomer"
                        >
                            <option value="">{translate("FlsValveConfigurator.Elastomer.Placeholder")}</option>
                            {availableElastomers.map(el => (
                                <option key={el.label} value={el.label}>
                                    {el.label}
                                    {el.quoteToOrder ? " (Quote to Order)" : ""}
                                </option>
                            ))}
                        </Select>
                        {selectedElastomerObj?.quoteToOrder ? (
                            <QtoHint>{translate("FlsValveConfigurator.QuoteToOrder.Hint")}</QtoHint>
                        ) : null}
                    </Field>

                    <Field>
                        <SectionLabel>{translate("FlsValveConfigurator.Paint.Label")}</SectionLabel>
                        <ReadOnlyValue>{readOnlyFields?.paint?.value?.trim() || "—"}</ReadOnlyValue>
                    </Field>

                    <SubmitRow>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={!canSubmit}
                            data-test-selector="valveGenerate"
                        >
                            {isSubmitting
                                ? translate("FlsValveConfigurator.Generating")
                                : translate("FlsValveConfigurator.Generate")}
                        </Button>
                    </SubmitRow>
                </FieldStack>

                <MobileStickySubmit>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={!canSubmit}
                        data-test-selector="valveGenerateMobile"
                    >
                        {isSubmitting
                            ? translate("FlsValveConfigurator.Generating")
                            : translate("FlsValveConfigurator.Generate")}
                    </Button>
                </MobileStickySubmit>
            </FormWrapper>
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ValveConfiguratorFormWidget),
    definition: {
        group: "Products",
        icon: "form",
        displayName: "Valve Configurator - Form",
        fieldDefinitions: [],
    },
};

export default widgetModule;
